import React, { useState } from 'react';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 18, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const COLUMNS = [
    { label: 'Booking ID', width: '120px' },
    { label: 'Passenger Name', width: '160px' },
    { label: 'Shuttle Route', width: '200px' },
    { label: 'Pickup Stop', width: '160px' },
    { label: 'Drop Stop', width: '160px' },
    { label: 'Date & Time', width: '160px' },
    { label: 'Seat No.', width: '90px' },
    { label: 'Status', width: '110px' },
    { label: 'Actions', width: '120px' },
];

export default function ShuttleBooking() {
    const [search, setSearch] = useState('');

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: FONT }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
                @keyframes shimmer-pulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
                .sb-header-btn:hover { background: rgba(255,255,255,0.25) !important; }
                .sb-search:focus { outline: none; box-shadow: 0 0 0 3px rgba(99,102,241,0.15); border-color: #6366f1 !important; }
            `}</style>

            {/* ── Banner ── */}
            <div style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)',
                padding: '32px 44px 36px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                <div style={{ position: 'absolute', bottom: -60, right: 120, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                <div style={{ position: 'relative', zIndex: 2 }}>
                    {/* Coming Soon Badge */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 20, padding: '5px 14px', fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 16, letterSpacing: '0.05em' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24', animation: 'shimmer-pulse 1.6s ease infinite' }} />
                        COMING SOON — IMPLEMENTATION PENDING
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                                    <Icon d={['M8 6v12', 'M16 6v12', 'M3 12h18', 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z']} size={24} color="white" />
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Trip Operations</div>
                                    <h1 style={{ fontSize: 28, fontWeight: 900, color: 'white', margin: 0, letterSpacing: '-0.02em' }}>Shuttle with Booking</h1>
                                </div>
                            </div>
                            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13.5, marginTop: 10, marginBottom: 0, maxWidth: 520 }}>
                                Manage scheduled shuttle services with seat-based bookings, route stops, and passenger assignments. Full booking engine coming soon.
                            </p>
                        </div>

                        {/* Stats pills (all zeroed out for now) */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Total Bookings', value: 0, color: '#a5b4fc' },
                                { label: 'Active Shuttles', value: 0, color: '#6ee7b7' },
                                { label: 'Today\'s Trips', value: 0, color: '#fde68a' },
                            ].map(s => (
                                <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 14, padding: '12px 20px', textAlign: 'center', minWidth: 90 }}>
                                    <div style={{ fontSize: 28, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div style={{ padding: '24px 44px 0', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {/* Search */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '0 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', flex: 1, maxWidth: 380 }}>
                    <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={15} color="#94a3b8" />
                    <input
                        className="sb-search"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search bookings..."
                        style={{ flex: 1, border: 'none', outline: 'none', padding: '11px 0', fontSize: 13.5, fontFamily: FONT, color: '#334155', background: 'transparent' }}
                        disabled
                    />
                </div>

                {/* Placeholder filters */}
                {['All Routes', 'All Status', 'Today'].map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, color: '#94a3b8', cursor: 'not-allowed', userSelect: 'none' }}>
                        {f} <Icon d="M6 9l6 6 6-6" size={13} color="#cbd5e1" />
                    </div>
                ))}

            </div>

            {/* ── Datatable ── */}
            <div style={{ padding: '20px 44px 44px', animation: 'fadeUp 0.35s ease' }}>
                <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

                    {/* Table header */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                                    <th style={{ padding: '14px 18px', width: 40 }}>
                                        <input type="checkbox" disabled style={{ accentColor: '#6366f1', cursor: 'not-allowed', opacity: 0.4 }} />
                                    </th>
                                    {COLUMNS.map(col => (
                                        <th key={col.label} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: col.label === 'Actions' ? 'right' : 'left', whiteSpace: 'nowrap', width: col.width }}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Empty state */}
                                <tr>
                                    <td colSpan={COLUMNS.length + 1}>
                                        <div style={{ padding: '64px 40px', textAlign: 'center' }}>
                                            <div style={{ width: 80, height: 80, borderRadius: 24, background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)', border: '2px solid #c7d2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                                <Icon d={['M8 6v12', 'M16 6v12', 'M3 12h18', 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z']} size={36} color="#6366f1" />
                                            </div>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                                                Shuttle Bookings
                                            </div>
                                            <div style={{ fontSize: 14, color: '#94a3b8', maxWidth: 400, margin: '0 auto 24px', lineHeight: 1.6 }}>
                                                The shuttle booking system is being built. Once implemented, all seat reservations, route assignments, and passenger schedules will appear here.
                                            </div>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, color: '#b45309' }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', animation: 'shimmer-pulse 1.6s ease infinite', display: 'inline-block' }} />
                                                Implementation Pending
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Table footer */}
                    <div style={{ padding: '14px 20px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>0 bookings total</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {['←', '1', '→'].map(p => (
                                <div key={p} style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: p === '1' ? '#6366f1' : '#94a3b8', background: p === '1' ? '#eef2ff' : 'white', cursor: 'not-allowed' }}>
                                    {p}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 14, fontSize: 12, color: '#94a3b8', textAlign: 'center', fontWeight: 500 }}>
                    🚌 Shuttle with Booking · Full implementation coming soon · Datatable structure is ready
                </div>
            </div>
        </div>
    );
}
